import { FormEvent, PointerEvent, ReactNode, Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { copies, languageNames, type Copy, type Lang } from './i18n'

const NeuralScene = lazy(() => import('./components/NeuralScene'))

gsap.registerPlugin(ScrollTrigger)

const languages = Object.keys(languageNames) as Lang[]
const navTargets = ['about', 'capabilities', 'system', 'work', 'careers', 'contact'] as const

function detectLanguage(): Lang {
  const stored = window.localStorage.getItem('abv-language') as Lang | null
  if (stored && languages.includes(stored)) return stored
  const browser = window.navigator.language.toLowerCase()
  if (browser.startsWith('de')) return 'de'
  if (browser.startsWith('es')) return 'es'
  if (browser.startsWith('zh')) return 'zh'
  if (browser.startsWith('th')) return 'th'
  if (browser.startsWith('ru')) return 'ru'
  return 'en'
}

function Logo() {
  return (
    <a className="brand" href="#top" aria-label="ABV Develop home">
      <span className="brand-mark"><i /><i /><i /></span>
      <span className="brand-word">ABV</span>
      <span className="brand-slash">//</span>
      <span className="brand-ai">AI</span>
    </a>
  )
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" />
    </svg>
  )
}

function CrossIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3v18M3 12h18" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.4" />
    </svg>
  )
}

function TiltCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const onMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'touch' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const element = ref.current
    if (!element) return
    const rect = element.getBoundingClientRect()
    const x = (event.clientX - rect.left) / rect.width - 0.5
    const y = (event.clientY - rect.top) / rect.height - 0.5
    element.style.setProperty('--tilt-x', `${(-y * 5.5).toFixed(2)}deg`)
    element.style.setProperty('--tilt-y', `${(x * 6.5).toFixed(2)}deg`)
    element.style.setProperty('--glow-x', `${((x + 0.5) * 100).toFixed(0)}%`)
    element.style.setProperty('--glow-y', `${((y + 0.5) * 100).toFixed(0)}%`)
  }
  const reset = () => {
    const element = ref.current
    if (!element) return
    element.style.setProperty('--tilt-x', '0deg')
    element.style.setProperty('--tilt-y', '0deg')
  }
  return <div ref={ref} onPointerMove={onMove} onPointerLeave={reset} className={`tilt-card ${className}`}>{children}</div>
}

function SystemDiagram({ copy }: { copy: ReturnType<typeof getSystemCopy> }) {
  return (
    <div className="system-diagram" data-reveal>
      <div className="system-orbit system-orbit-a" />
      <div className="system-orbit system-orbit-b" />
      <div className="system-core">
        <span className="system-core-pulse" />
        <span className="system-core-label">{copy.core}</span>
        <strong>{copy.human}</strong>
      </div>
      {copy.nodes.map((node, index) => (
        <div className={`system-node system-node-${index + 1}`} key={node.title}>
          <span>0{index + 1}</span>
          <strong>{node.title}</strong>
          <p>{node.body}</p>
        </div>
      ))}
    </div>
  )
}

function getSystemCopy(copy: ReturnType<typeof getCopy>) {
  return copy.system
}

function getCopy(lang: Lang) {
  return copies[lang]
}

function PlanetCompetencyOverlay({ copy }: { copy: Copy['universe'] }) {
  return (
    <div className="planet-competency-overlay" aria-hidden="true">
      <div className="planet-overlay-status"><span />{copy.orbitLabel}</div>
      {copy.satellites.map((satellite, index) => {
        const style = {
          '--satellite-delay': `${-index * 0.7}s`,
        } as React.CSSProperties
        return (
          <div className={`planet-satellite-label domain-${satellite.domain}`} style={style} key={satellite.code}>
            <i /><span>{satellite.code}</span><strong>{satellite.title}</strong>
          </div>
        )
      })}
    </div>
  )
}

function CompetencyUniverse({ copy }: { copy: Copy['universe'] }) {
  return (
    <section className="section-shell universe-section" id="capabilities">
      <div className="section-index" aria-hidden="true">02 — COMPETENCY UNIVERSE</div>
      <div className="universe-intro" data-reveal>
        <div>
          <div className="eyebrow">{copy.eyebrow}</div>
          <h2>{copy.title}</h2>
        </div>
        <p>{copy.body}</p>
      </div>
      <div className="universe-domain-grid">
        {copy.domains.map((domain) => (
          <TiltCard className={`universe-domain domain-${domain.id}`} key={domain.id}>
            <div className="universe-domain-top">
              <span>{domain.code}</span>
              <div className="domain-orb"><i /><i /><i /></div>
            </div>
            <h3>{domain.title}</h3>
            <p>{domain.body}</p>
            <ol>
              {domain.items.map((item, index) => (
                <li key={item}><span>{String(index + 1).padStart(2, '0')}</span><strong>{item}</strong></li>
              ))}
            </ol>
          </TiltCard>
        ))}
      </div>
    </section>
  )
}

type ContactStatus = 'idle' | 'sending' | 'sent' | 'error'

function App() {
  const [lang, setLang] = useState<Lang>(() => detectLanguage())
  const [menuOpen, setMenuOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('about')
  const [scrollProgress, setScrollProgress] = useState(0)
  const [contactMessage, setContactMessage] = useState('')
  const [contactStatus, setContactStatus] = useState<ContactStatus>('idle')
  const contactForm = useRef<HTMLFormElement>(null)
  const copy = useMemo(() => getCopy(lang), [lang])

  useEffect(() => {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : lang
    document.title = copy.meta.title
    const meta = document.querySelector('meta[name="description"]')
    meta?.setAttribute('content', copy.meta.description)
    window.localStorage.setItem('abv-language', lang)
  }, [copy.meta.description, copy.meta.title, lang])

  useEffect(() => {
    const onScroll = () => {
      const max = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1)
      setScrollProgress(Math.min(1, window.scrollY / max))
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && setActiveSection(entry.target.id)),
      { rootMargin: '-35% 0px -55%', threshold: 0.01 },
    )
    navTargets.forEach((id) => {
      const element = document.getElementById(id)
      if (element) observer.observe(element)
    })
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const context = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>('[data-reveal]').forEach((element) => {
        gsap.fromTo(
          element,
          { autoAlpha: 0, y: 54, filter: 'blur(12px)' },
          {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: 1.05,
            ease: 'power3.out',
            scrollTrigger: { trigger: element, start: 'top 88%', once: true },
          },
        )
      })
      gsap.utils.toArray<HTMLElement>('[data-parallax]').forEach((element) => {
        gsap.to(element, {
          yPercent: -14,
          ease: 'none',
          scrollTrigger: { trigger: element, scrub: 0.8, start: 'top bottom', end: 'bottom top' },
        })
      })
    })
    return () => context.revert()
  }, [lang])

  const setLanguage = (next: Lang) => {
    setLang(next)
    setLanguageOpen(false)
    document.body.classList.remove('language-transition')
    requestAnimationFrame(() => document.body.classList.add('language-transition'))
  }

  const navigate = (id: string) => {
    setMenuOpen(false)
    document.getElementById(id)?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
  }

  const selectCareerRole = (role: string) => {
    setContactMessage(role)
    setContactStatus('idle')
    navigate('contact')
    window.setTimeout(() => contactForm.current?.querySelector<HTMLTextAreaElement>('textarea[name="message"]')?.focus(), 450)
  }

  const submitContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (contactStatus === 'sending') return
    const element = event.currentTarget
    const form = new FormData(element)
    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), 20_000)
    setContactStatus('sending')
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: String(form.get('name') || ''),
          email: String(form.get('email') || ''),
          message: String(form.get('message') || ''),
          website: String(form.get('website') || ''),
          locale: lang,
        }),
        signal: controller.signal,
      })
      const result = await response.json().catch(() => null)
      if (!response.ok || !result?.ok) throw new Error('submission_failed')
      element.reset()
      setContactMessage('')
      setContactStatus('sent')
    } catch {
      setContactStatus('error')
    } finally {
      window.clearTimeout(timeout)
    }
  }

  return (
    <div className={`app language-${lang}`} id="top">
      <Suspense fallback={<div className="neural-scene neural-scene-fallback" aria-hidden="true" />}>
        <NeuralScene />
      </Suspense>
      <div className="ambient-grid" aria-hidden="true" />
      <div className="noise" aria-hidden="true" />
      <div className="scroll-progress" style={{ transform: `scaleX(${scrollProgress})` }} aria-hidden="true" />

      <header className="site-header">
        <Logo />
        <nav className={menuOpen ? 'nav-open' : ''} aria-label="Primary navigation">
          {navTargets.map((id) => (
            <button className={activeSection === id ? 'active' : ''} key={id} onClick={() => navigate(id)}>
              {copy.nav[id]}
            </button>
          ))}
        </nav>
        <div className="header-actions">
          <div className="language-switcher">
            <button className="language-current" onClick={() => setLanguageOpen((value) => !value)} aria-expanded={languageOpen} aria-label="Select language">
              {lang.toUpperCase()}<span>⌄</span>
            </button>
            {languageOpen && (
              <div className="language-menu">
                {languages.map((item) => (
                  <button className={item === lang ? 'selected' : ''} key={item} onClick={() => setLanguage(item)}>
                    <span>{item.toUpperCase()}</span>{languageNames[item]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="menu-toggle" onClick={() => setMenuOpen((value) => !value)} aria-expanded={menuOpen} aria-label="Toggle menu">
            <span /><span />
          </button>
        </div>
      </header>

      <main key={lang}>
        <section className="hero section-shell">
          <div className="hero-copy">
            <div className="eyebrow hero-enter enter-1"><span className="eyebrow-dot" />{copy.hero.kicker}</div>
            <h1 className="hero-enter enter-2">
              <span>{copy.hero.titleA}</span>
              <em>{copy.hero.titleAccent}</em>
              <span>{copy.hero.titleB}</span>
            </h1>
            <p className="hero-body hero-enter enter-3">{copy.hero.body}</p>
            <div className="hero-actions hero-enter enter-4">
              <button className="button button-primary" onClick={() => navigate('contact')}>{copy.hero.primary}<ArrowIcon /></button>
              <button className="button button-ghost" onClick={() => navigate('system')}>{copy.hero.secondary}</button>
            </div>
          </div>

          <div className="hero-terminal hero-enter enter-5" data-parallax>
            <div className="terminal-top"><span /><span /><span /><b>abv.intelligence / live</b></div>
            <div className="terminal-body">
              {copy.hero.terminal.map((line, index) => (
                <div key={line} style={{ '--delay': `${index * 0.42}s` } as React.CSSProperties}>
                  <span>{index === copy.hero.terminal.length - 1 ? '◆' : '›'}</span>{line}<i />
                </div>
              ))}
            </div>
          </div>

          <PlanetCompetencyOverlay copy={copy.universe} />
          <div className="mobile-satellite-strip" aria-label={copy.universe.orbitLabel}>
            {copy.universe.satellites.map((satellite) => (
              <div className={`mobile-satellite domain-${satellite.domain}`} key={satellite.code}>
                <i /><span>{satellite.code}</span><strong>{satellite.title}</strong>
              </div>
            ))}
          </div>

          <div className="hero-live"><span /><b>{copy.hero.live}</b></div>
          <button className="scroll-cue" onClick={() => navigate('about')}><span>{copy.hero.scroll}</span><i /></button>
        </section>

        <section className="stats-band" aria-label="Company facts">
          {copy.stats.map((stat) => (
            <div key={stat.label} data-reveal><strong>{stat.value}</strong><span>{stat.label}</span></div>
          ))}
        </section>

        <section className="section-shell manifesto" id="about">
          <div className="section-index" aria-hidden="true">01 — IDENTITY</div>
          <div className="manifesto-copy" data-reveal>
            <div className="eyebrow">{copy.about.eyebrow}</div>
            <h2>{copy.about.title}</h2>
            <p>{copy.about.body}</p>
          </div>
          <TiltCard className="manifesto-card">
            <div className="quote-mark">“</div>
            <blockquote>{copy.about.quote}</blockquote>
            <div className="code-line"><span>const</span> accountability = <b>true</b>;</div>
            <p>{copy.about.code}</p>
          </TiltCard>
        </section>

        <section className="section-shell process-section">
          <div className="section-heading" data-reveal>
            <div className="eyebrow">{copy.process.eyebrow}</div>
            <h2>{copy.process.title}</h2>
            <p>{copy.process.body}</p>
          </div>
          <div className="process-grid">
            {copy.process.steps.map((step) => (
              <article key={step.index} data-reveal>
                <div className="process-line"><span /><i /></div>
                <b>{step.index}</b><h3>{step.title}</h3><p>{step.body}</p>
              </article>
            ))}
          </div>
        </section>

        <CompetencyUniverse copy={copy.universe} />

        <section className="section-shell services-section">
          <div className="section-index" aria-hidden="true">03 — DELIVERY CAPABILITIES</div>
          <div className="section-heading wide" data-reveal>
            <div className="eyebrow">{copy.services.eyebrow}</div>
            <h2>{copy.services.title}</h2>
            <p>{copy.services.body}</p>
          </div>
          <div className="services-grid">
            {copy.services.items.map((service) => (
              <TiltCard className="service-card" key={service.code}>
                <div className="service-top"><span>{service.code}</span><CrossIcon /></div>
                <h3>{service.title}</h3><p>{service.body}</p>
                <div className="tags">{service.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
              </TiltCard>
            ))}
          </div>
        </section>

        <section className="system-section" id="system">
          <div className="section-shell system-layout">
            <div className="system-copy" data-reveal>
              <div className="eyebrow">{copy.system.eyebrow}</div>
              <h2>{copy.system.title}</h2>
              <p>{copy.system.body}</p>
              <div className="system-legend"><span><i className="cyan" />AI acceleration</span><span><i className="violet" />Deterministic control</span></div>
            </div>
            <SystemDiagram copy={copy.system} />
          </div>
        </section>

        <section className="section-shell work-section" id="work">
          <div className="section-index" aria-hidden="true">04 — SYSTEM DOMAINS</div>
          <div className="section-heading wide" data-reveal>
            <div className="eyebrow">{copy.work.eyebrow}</div>
            <h2>{copy.work.title}</h2>
            <p>{copy.work.body}</p>
          </div>
          <div className="work-list">
            {copy.work.cases.map((item) => (
              <TiltCard className="work-card" key={item.index}>
                <div className="work-number">{item.index}</div>
                <div className="work-main"><span>{item.sector}</span><h3>{item.title}</h3><p>{item.body}</p></div>
                <div className="work-capabilities">{item.capabilities.map((capability) => <span key={capability}>{capability}</span>)}</div>
                <div className="work-arrow"><ArrowIcon /></div>
              </TiltCard>
            ))}
          </div>
          <p className="work-note" data-reveal>{copy.work.note}</p>
        </section>

        <section className="section-shell careers-section" id="careers">
          <div className="career-panel" data-reveal>
            <div className="career-copy"><div className="eyebrow">{copy.careers.eyebrow}</div><h2>{copy.careers.title}</h2><p>{copy.careers.body}</p><span>{copy.careers.location}</span></div>
            <div className="career-roles">
              {copy.careers.roles.map((role, index) => (
                <button key={role} type="button" onClick={() => selectCareerRole(role)}>
                  <span>0{index + 1}</span><strong>{role}</strong><small>{copy.careers.apply}</small><ArrowIcon />
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="contact-section" id="contact">
          <div className="contact-radiance" aria-hidden="true" />
          <div className="section-shell contact-layout">
            <div className="contact-copy" data-reveal>
              <div className="eyebrow">{copy.contact.eyebrow}</div><h2>{copy.contact.title}</h2><p>{copy.contact.body}</p>
              <div className="contact-details">
                <div><span>{copy.contact.email}</span><strong>sales@abvdevelop.com</strong></div>
                <div><span>{copy.contact.location}</span><strong>279/365–368 Moo 12, Nongprue<br />Bang Lamung, Chon Buri 20150</strong></div>
              </div>
            </div>
            <form ref={contactForm} className="contact-form" onSubmit={submitContact} data-reveal>
              <label><span>{copy.contact.nameLabel}</span><input name="name" required placeholder={copy.contact.namePlaceholder} autoComplete="name" /></label>
              <label><span>{copy.contact.emailLabel}</span><input name="email" required type="email" placeholder={copy.contact.emailPlaceholder} autoComplete="email" /></label>
              <label className="form-honeypot" aria-hidden="true">Website<input name="website" tabIndex={-1} autoComplete="off" /></label>
              <label><span>{copy.contact.messageLabel}</span><textarea name="message" required rows={5} placeholder={copy.contact.messagePlaceholder} value={contactMessage} onChange={(event) => { setContactMessage(event.target.value); if (contactStatus !== 'idle') setContactStatus('idle') }} /></label>
              <button className="button button-primary" type="submit" disabled={contactStatus === 'sending'}>{contactStatus === 'sending' ? copy.contact.sending : copy.contact.send}<ArrowIcon /></button>
              {contactStatus !== 'idle' && <p className={`form-status form-status-${contactStatus}`} role="status" aria-live="polite">{contactStatus === 'sent' ? copy.contact.success : contactStatus === 'error' ? copy.contact.error : copy.contact.sending}</p>}
              <small>{copy.contact.note}</small>
            </form>
          </div>
        </section>
      </main>

      <footer>
        <div className="section-shell footer-inner">
          <Logo />
          <p>{copy.footer.tagline}</p>
          <div><span>© {new Date().getFullYear()} {copy.footer.rights}</span><button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>{copy.footer.top} ↑</button></div>
        </div>
      </footer>
    </div>
  )
}

export default App
